import { AnimatePresence, motion } from "framer-motion";
import { ListFilterIcon, XIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export interface ActiveFilter {
	key: string;
	label: string;
	value: string;
	clear: () => void;
}

/**
 * The Filter toggle with its count badge, the panel holding the caller's fields, and the
 * applied filters as removable chips. Opens on load when a filter is already applied, so
 * it isn't hidden behind the toggle.
 */
export function FilterPanel({
	activeFilters,
	canClear = activeFilters.length > 0,
	onClearAll,
	children,
}: {
	activeFilters: ActiveFilter[];
	/** true while a typed search is still debouncing, before it shows up as a chip */
	canClear?: boolean;
	onClearAll: () => void;
	children: ReactNode;
}) {
	const { t } = useTranslation("bonus-ro");
	const [open, setOpen] = useState(activeFilters.length > 0);

	return (
		<>
			<div>
				<Button
					type="button"
					variant={open ? "default" : "outline"}
					className="shadow-none"
					onClick={() => setOpen((prev) => !prev)}
				>
					<ListFilterIcon />
					{open ? t("filter_panel.close") : t("filter_panel.filter")}
					{activeFilters.length > 0 && (
						<Badge variant="accent" className="px-1 rounded-full">
							{activeFilters.length}
						</Badge>
					)}
				</Button>
			</div>

			<AnimatePresence initial={false}>
				{open ? (
					<motion.section
						key="filters"
						initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
						animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
						exit={{ opacity: 0, y: -10, filter: "blur(8px)" }}
						transition={{ duration: 0.25, ease: "easeOut" }}
						className="p-3 border shadow-inner rounded-xl bg-background"
					>
						{children}

						{activeFilters.length > 0 && (
							<div className="flex flex-wrap items-center gap-2 mt-6">
								<span className="text-xs font-medium text-muted-foreground">
									{t("filter_panel.active")}:
								</span>
								{activeFilters.map((filter) => (
									<Badge
										key={filter.key}
										variant="secondary"
										className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-[10px] uppercase tracking-wide"
									>
										<span>{filter.label}:</span>
										<span className="font-bold">{filter.value}</span>
										<button
											type="button"
											aria-label={t("filter_panel.remove", {
												label: filter.label,
											})}
											onClick={filter.clear}
											className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted-foreground/15"
										>
											<XIcon className="h-3 w-3" />
										</button>
									</Badge>
								))}
								<Button
									variant="destructive-outline"
									size="sm"
									className="rounded-full"
									onClick={onClearAll}
								>
									{t("filter_panel.clear")}
								</Button>
							</div>
						)}

						<div className="flex flex-col justify-end gap-4 mt-4 md:gap-2 md:flex-row">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="w-full md:w-fit"
								onClick={() => setOpen(false)}
							>
								{t("filter_panel.close")}
							</Button>
							{canClear ? (
								<Button
									type="button"
									variant="ghost-destructive"
									size="sm"
									className="w-full md:w-fit"
									onClick={onClearAll}
								>
									<XIcon className="size-4" />
									{t("filter_panel.clear")}
								</Button>
							) : null}
						</div>
					</motion.section>
				) : null}
			</AnimatePresence>
		</>
	);
}
